/**
 * researchFeatures.js
 *
 * Einzige Quelle der Wahrheit für die Research-Lizenzen der World-DB.
 *
 * Jedes Research-Tool hat ein eigenes Flag, das ausschliesslich im
 * Team-Dashboard (PremiumToggle) gesetzt wird. Die Flags liegen — wie
 * hasDealWizard — IMMER beim Owner der aktiven World unter
 * Users/{activeWorldId}/…, nie beim eingeloggten User. Team-Mitarbeiter
 * erben die Freischaltung ihres Owners dadurch automatisch, sobald sie in
 * dessen World arbeiten (siehe useResearchAccess).
 *
 * Ausnahme: Features mit `minPlan` sind ab diesem Membership-Plan automatisch
 * an. Ein im Team-Dashboard gesetztes true/false überschreibt den Plan.
 *
 * Nicht gelistete Tools sind bewusst ableitbar statt einzeln lizenziert:
 *   Research + Find Emails  = Web Research + DM Email Finder
 *   Ultimate Research       = Potential Analyser + Web Research + DM Email Finder
 */

import { PLAN_IDS } from './creditPlans';

export const RESEARCH_SUPPORT_EMAIL = 'julian@bonusclub.io';

export const RESEARCH_LOCKED_TITLE = 'This feature is not available for your account';

// tone: Farbvariante der Chips im Team-Dashboard (premium-toggle-chip-<tone>).
export const RESEARCH_FEATURES = [
  {
    key: 'hasWebResearch',
    id: 'web_intelligence',
    name: 'Web Research',
    short: 'Web',
    icon: 'fa-globe',
    tone: 'blue',
  },
  {
    key: 'hasWebsiteAnalyzer',
    id: 'website_analyzer',
    name: 'Website Analyzer',
    short: 'Site',
    icon: 'fa-laptop-code',
    tone: 'cyan',
  },
  {
    key: 'hasInstagramResearch',
    id: 'instagram_research',
    name: 'Instagram Research',
    short: 'IG',
    icon: 'fa-camera-retro',
    tone: 'pink',
  },
  {
    key: 'hasGoogleReviewAudit',
    id: 'google_review_audit',
    name: 'Google Review Audit',
    short: 'Reviews',
    icon: 'fa-star-half-stroke',
    tone: 'amber',
  },
  {
    key: 'hasSeoAnalyzer',
    id: 'seo_analyzer',
    name: 'SEO Analyzer',
    short: 'SEO',
    icon: 'fa-magnifying-glass-chart',
    tone: 'violet',
  },
  {
    key: 'hasPotentialAnalyzer',
    id: 'potential_analyzer',
    name: 'Potential Analyser',
    short: 'Score',
    icon: 'fa-bullseye',
    tone: 'amber',
  },
  {
    key: 'hasDmEmailFinder',
    id: 'dm_email_finder',
    name: 'DM Email Finder',
    short: 'DM',
    icon: 'fa-crosshairs',
    tone: 'emerald',
  },
  {
    key: 'hasDataConnectors',
    id: 'data_connectors',
    name: 'Custom Researchers',
    short: 'API',
    icon: 'fa-plug',
    tone: 'cyan',
    minPlan: 'pro',
  },
];

export const RESEARCH_FEATURE_KEYS = RESEARCH_FEATURES.map((f) => f.key);

/** Features that switch on by membership plan unless the Team Dashboard overrides them. */
export const RESEARCH_PLAN_KEYS = RESEARCH_FEATURES.filter((f) => f.minPlan).map((f) => f.key);

/** Default for `key` on this plan: on from the feature's `minPlan` upwards. */
export function researchPlanDefault(key, planId) {
  const feature = RESEARCH_FEATURES.find((f) => f.key === key);
  if (!feature?.minPlan || !planId) return false;
  const at = PLAN_IDS.indexOf(planId);
  return at >= 0 && at >= PLAN_IDS.indexOf(feature.minPlan);
}

/**
 * Effective licence. An explicit true/false from the Team Dashboard wins;
 * a missing flag falls back to the plan default (false without `minPlan`).
 * Twin: functions/dataConnector.js → hasLicense.
 */
export function resolveResearchFlag(key, raw, planId) {
  if (raw === true || raw === false) return raw;
  return researchPlanDefault(key, planId);
}

export const RESEARCH_ALL_LOCKED = RESEARCH_FEATURE_KEYS.reduce(
  (acc, key) => ({ ...acc, [key]: false }),
  {}
);

/**
 * Vorbefüllte Freischalt-Anfrage. Account und World-ID stehen mit in der Mail,
 * damit die Anfrage ohne Rückfrage im Dashboard zugeordnet werden kann.
 */
export function buildResearchUnlockMailto({ featureName, accountEmail, worldId } = {}) {
  const subject = featureName
    ? `Access request: ${featureName}`
    : 'Access request: Research Features';

  const body = [
    'Hi Julian,',
    '',
    featureName
      ? `please unlock "${featureName}" for our account.`
      : 'please unlock the Research Features for our account.',
    '',
    accountEmail ? `Account: ${accountEmail}` : null,
    worldId ? `World ID: ${worldId}` : null,
  ]
    .filter((line) => line !== null)
    .join('\n');

  return `mailto:${RESEARCH_SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
